// ============================================
// Centralized Pricing Module — Single Source of Truth
// ============================================
// All pricing constants, types, and calculation logic live here.
// Marketing site, native app, and in-app billing all import from this module.
// Base prices are in USD. PPP is applied at the display/conversion layer.

// --- Plan Tiers ---

export const STANDARD_BRANCH_USD_RATE = 159
export const STANDARD_MAX_BRANCHES = 5

export type PricingPlanTier = 'standard' | 'custom'

export interface TierDefinition {
  id: PricingPlanTier
  title: string
  tagline: string
  branchRange: string
  baseUsdRate: number | null // null = custom quote
  features: string[]
  supplierProTease: string
  automatedTease: { perMonth: number }
}

export const TIERS: Record<PricingPlanTier, TierDefinition> = {
  standard: {
    id: 'standard',
    title: 'Standard',
    tagline: 'For pharmacy teams running procurement across up to five branches.',
    branchRange: '1-5 branches',
    baseUsdRate: STANDARD_BRANCH_USD_RATE,
    features: [
      'AI procurement assistant',
      'Low-stock and expiry risk alerts',
      'Recommended purchase lists',
      'Supplier comparison and quote context',
      'Branch-level procurement views',
      'CSV, spreadsheet, and supported POS imports',
      'Same-day guided setup',
    ],
    supplierProTease: 'Supplier comparison and quote context included',
    automatedTease: { perMonth: 1 },
  },
  custom: {
    id: 'custom',
    title: 'Custom',
    tagline: 'For pharmacy networks with more than five branches, rollout support, and custom controls.',
    branchRange: '6+ branches',
    baseUsdRate: null,
    features: [
      'Everything in Standard',
      'Custom branch rollout plan',
      'Central approval and governance support',
      'Advanced procurement insights',
      'Custom integrations',
      'Dedicated onboarding and support',
      'Commercial terms matched to your network',
    ],
    supplierProTease: 'Custom supplier sourcing workflow',
    automatedTease: { perMonth: 5 },
  },
}

export const TIER_ORDER: PricingPlanTier[] = ['standard', 'custom']

// --- Volume Discounts ---

export interface VolumeDiscount {
  minBranches: number
  maxBranches: number
  usdRate: number
}

export const VOLUME_DISCOUNTS: VolumeDiscount[] = []

// --- Add-ons ---

export type AddOnId = 'supplier_network_pro'

export interface AddOnDefinition {
  id: AddOnId
  label: string
  usdPricePerBranch: number
  features: string[]
}

export const SUPPLIER_NETWORK_PRO: AddOnDefinition = {
  id: 'supplier_network_pro',
  label: 'Supplier Network Pro',
  usdPricePerBranch: 30,
  features: [
    'Unlimited supplier discovery and matching',
    'Real-time price comparison across all connected suppliers',
    'Quote aggregation and best-price recommendations',
    'Supplier performance scoring and trends',
    'AI-optimised procurement lists',
    'Expiry redistribution suggestions between branches',
  ],
}

export const ADD_ONS: Record<AddOnId, AddOnDefinition> = {
  supplier_network_pro: SUPPLIER_NETWORK_PRO,
}

// --- Automated Procurement (Usage-based) ---

export const AUTOMATED_PROCUREMENT = {
  revenueShareRate: 0.01, // 1% of autonomous procurement spend
  label: 'Automated Procurement',
  description: 'Fey handles procurement autonomously. Includes everything in Supplier Network Pro.',
  features: [
    'Everything in Supplier Network Pro (included)',
    'Autonomous order placement at reorder points',
    'Rule-based thresholds and budget caps',
    'Scheduled procurement cycles (daily/weekly)',
    'Auto-negotiation with connected suppliers',
    'Exception-only workflow — intervene only when needed',
    'Full audit trail of every autonomous decision',
  ],
} as const

// --- PPP / Regional Pricing ---

export interface MarketProfile {
  id: string
  label: string
  currency: string
  pppFactor: number
  flag: string
  branchPriceLocal?: number
}

export const PPP_MARKETS: MarketProfile[] = [
  { id: 'NG', label: 'Nigeria', currency: 'NGN', pppFactor: 0.61, branchPriceLocal: 150_000, flag: '🇳🇬' },
  { id: 'GH', label: 'Ghana', currency: 'GHS', pppFactor: 0.65, branchPriceLocal: 1_600, flag: '🇬🇭' },
  { id: 'KE', label: 'Kenya', currency: 'KES', pppFactor: 0.65, branchPriceLocal: 13_500, flag: '🇰🇪' },
  { id: 'ZA', label: 'South Africa', currency: 'ZAR', pppFactor: 0.80, branchPriceLocal: 2_400, flag: '🇿🇦' },
  { id: 'US', label: 'United States', currency: 'USD', pppFactor: 1, branchPriceLocal: 159, flag: '🇺🇸' },
  { id: 'GB', label: 'United Kingdom', currency: 'GBP', pppFactor: 1, branchPriceLocal: 129, flag: '🇬🇧' },
  { id: 'DE', label: 'Germany', currency: 'EUR', pppFactor: 1, branchPriceLocal: 149, flag: '🇩🇪' },
  { id: 'IN', label: 'India', currency: 'INR', pppFactor: 0.60, branchPriceLocal: 8_500, flag: '🇮🇳' },
  { id: 'AE', label: 'UAE', currency: 'AED', pppFactor: 1, branchPriceLocal: 599, flag: '🇦🇪' },
]

export const DEFAULT_MARKET_ID = 'US'

export const FALLBACK_FX_RATES: Record<string, number> = {
  USD: 1,
  NGN: 1550,
  GHS: 15.74,
  KES: 128.9,
  ZAR: 18.53,
  EUR: 0.96,
  GBP: 0.82,
  INR: 87.29,
  AED: 3.67,
}

// --- Calculator Inputs & Savings ---

export interface CalculatorInputs {
  branches: number
  monthlyDrugSpend: number       // in user's local currency
  monthlyExpiryLoss: number      // in user's local currency
  monthlyStockoutLoss: number    // in user's local currency
  procurementStaffMonthlySalary: number // in user's local currency
  weeklyProcurementHours: number
  supplierCount: number
}

export interface SavingsBreakdown {
  drugSpendSavings: number
  expirySavings: number
  stockoutRecovery: number
  laborSavings: number
  supplierMgmtSavings: number
  totalMonthlySavings: number
}

export const SAVINGS_RATES = {
  drugSpendOptimisation: 0.05,  // 5% of monthly drug spend
  expiryReduction: 0.40,       // 40% reduction in expiry losses
  stockoutRecovery: 0.18,      // 18% recovery of stockout losses
  laborEfficiency: 0.35,       // 35% reduction in procurement labor
  supplierOverheadPerSupplier: 0.50, // 50% reduction in per-supplier overhead
} as const

export const DEFAULT_CALCULATOR_INPUTS: CalculatorInputs = {
  branches: 3,
  monthlyDrugSpend: 3_000,        // USD — global neutral baseline
  monthlyExpiryLoss: 250,
  monthlyStockoutLoss: 300,
  procurementStaffMonthlySalary: 800,
  weeklyProcurementHours: 25,
  supplierCount: 8,
}

// Estimated monthly overhead per supplier (management, communication, reconciliation)
const SUPPLIER_OVERHEAD_PER_MONTH = 10 // USD baseline

// --- Comparison table ---

export interface ComparisonRow {
  feature: string
  standard: string
  custom: string
}

export const PRICING_COMPARISON_ROWS: ComparisonRow[] = [
  {
    feature: 'Branch range',
    standard: '1-5 branches',
    custom: '6+ branches',
  },
  {
    feature: 'AI chat assistant',
    standard: 'Included',
    custom: 'Included',
  },
  {
    feature: 'Procurement lists',
    standard: 'Recommended purchase lists',
    custom: 'Network-wide purchase workflows',
  },
  {
    feature: 'Low-stock & expiry alerts',
    standard: 'Included',
    custom: 'Advanced branch-level alerts',
  },
  {
    feature: 'Supplier intelligence',
    standard: 'Supplier comparison and quote context',
    custom: 'Custom supplier sourcing workflow',
  },
  {
    feature: 'Supplier Network Pro',
    standard: '+{supplier_pro_price} / branch / mo',
    custom: 'Custom',
  },
  {
    feature: 'Automated procurement',
    standard: '1 free / mo, then 1% of spend',
    custom: 'Custom included volume',
  },
  {
    feature: 'Operations controls',
    standard: 'Branch and team views',
    custom: 'Governance controls and rollout planning',
  },
  {
    feature: 'Data sources & integrations',
    standard: 'CSV, spreadsheets, supported POS imports',
    custom: 'Custom integrations',
  },
  {
    feature: 'Support',
    standard: 'Same-day guided setup',
    custom: 'Dedicated onboarding + support',
  },
]

// --- Calculation Functions ---

function clampPositive(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.round(value))
}

export function getBranchRate(branches: number): number {
  return branches <= STANDARD_MAX_BRANCHES ? STANDARD_BRANCH_USD_RATE : 0
}

export function getTierForBranches(branches: number): PricingPlanTier {
  return branches <= STANDARD_MAX_BRANCHES ? 'standard' : 'custom'
}

export interface SubscriptionResult {
  branches: number
  tier: PricingPlanTier
  branchRateUsd: number
  branchBaseUsd: number
  addOnCostUsd: number
  monthlyUsd: number
  annualUsd: number
  isCustomPricing: boolean
}

export function calculateSubscription(
  branches: number,
  addOns: AddOnId[] = [],
): SubscriptionResult {
  const b = Math.max(1, clampPositive(branches))
  const isCustomPricing = b > STANDARD_MAX_BRANCHES
  const paidBranches = isCustomPricing ? 0 : b
  const branchRate = getBranchRate(b)
  const branchBase = paidBranches * branchRate
  const addOnCost = paidBranches * addOns.reduce(
    (sum, id) => sum + (ADD_ONS[id]?.usdPricePerBranch ?? 0),
    0,
  )
  const monthly = branchBase + addOnCost
  return {
    branches: b,
    tier: getTierForBranches(b),
    branchRateUsd: branchRate,
    branchBaseUsd: branchBase,
    addOnCostUsd: addOnCost,
    monthlyUsd: monthly,
    annualUsd: monthly * 12,
    isCustomPricing,
  }
}

export function calculateSavings(inputs: CalculatorInputs): SavingsBreakdown {
  const drugSpend = clampPositive(inputs.monthlyDrugSpend)
  const expiryLoss = clampPositive(inputs.monthlyExpiryLoss)
  const stockoutLoss = clampPositive(inputs.monthlyStockoutLoss)
  const staffSalary = clampPositive(inputs.procurementStaffMonthlySalary)
  const hours = clampPositive(inputs.weeklyProcurementHours)
  const suppliers = clampPositive(inputs.supplierCount)

  const drugSpendSavings = Math.round(drugSpend * SAVINGS_RATES.drugSpendOptimisation)
  const expirySavings = Math.round(expiryLoss * SAVINGS_RATES.expiryReduction)
  const stockoutRecovery = Math.round(stockoutLoss * SAVINGS_RATES.stockoutRecovery)

  // Labor savings: proportion of salary attributable to procurement hours × efficiency rate
  // Assumes 40-hour work week
  const procurementFraction = hours > 0 ? Math.min(1, hours / 40) : 0
  const laborSavings = Math.round(staffSalary * procurementFraction * SAVINGS_RATES.laborEfficiency)

  // Supplier management overhead savings
  const currentOverhead = suppliers * SUPPLIER_OVERHEAD_PER_MONTH
  const supplierMgmtSavings = Math.round(currentOverhead * SAVINGS_RATES.supplierOverheadPerSupplier)

  const totalMonthlySavings =
    drugSpendSavings + expirySavings + stockoutRecovery + laborSavings + supplierMgmtSavings

  return {
    drugSpendSavings,
    expirySavings,
    stockoutRecovery,
    laborSavings,
    supplierMgmtSavings,
    totalMonthlySavings,
  }
}

// --- Currency & PPP Helpers ---

export function applyPpp(usdAmount: number, pppFactor: number): number {
  return Math.max(0, Math.round(usdAmount * pppFactor))
}

export function usdToLocal(
  usdAmount: number,
  currency: string,
  fxRates: Record<string, number>,
): number {
  const rate = fxRates[currency] ?? 1
  return Math.max(0, Math.round(usdAmount * rate))
}

function roundLocalPrice(value: number, currency: string): number {
  if (currency === 'USD' || currency === 'GBP' || currency === 'EUR' || currency === 'AED') {
    return Math.max(0, Math.round(value / 10) * 10 - 1)
  }
  if (value >= 100_000) return Math.max(0, Math.round(value / 10_000) * 10_000)
  if (value >= 10_000) return Math.max(0, Math.round(value / 500) * 500)
  if (value >= 1_000) return Math.max(0, Math.round(value / 100) * 100)
  return Math.max(0, Math.round(value))
}

export function getLocalizedBranchPrice(
  market: MarketProfile,
  fxRates: Record<string, number> = FALLBACK_FX_RATES,
): number {
  if (market.branchPriceLocal !== undefined) return market.branchPriceLocal
  const converted = usdToLocal(STANDARD_BRANCH_USD_RATE, market.currency, fxRates)
  const adjusted = converted * Math.min(1, Math.max(0.35, market.pppFactor))
  return roundLocalPrice(adjusted, market.currency)
}

export function localToUsd(
  localAmount: number,
  currency: string,
  fxRates: Record<string, number>,
): number {
  const rate = fxRates[currency] ?? 1
  if (rate === 0) return 0
  return Math.max(0, Math.round(localAmount / rate))
}

export function getMarketProfile(
  marketId: string,
  markets: MarketProfile[] = PPP_MARKETS,
): MarketProfile {
  return (
    markets.find((m) => m.id === marketId) ??
    markets[0] ??
    PPP_MARKETS[0]
  )
}

export function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value)
}
